import { useId, type ReactNode } from "react";

/** Field-keyed messages from the server, shown under the matching input. */
export type FieldErrors = Record<string, string>;

/**
 * Labelled inputs grouped into titled sections, two columns on wider screens.
 * Shared by the record edit forms (students, staff) and the finance forms.
 */
export function Field({
  label,
  name,
  errors,
  hint,
  className = "",
  optional,
  as = "label",
  children,
}: {
  label: string;
  /** With `errors`, the server message for this field replaces the hint. */
  name?: string;
  errors?: FieldErrors;
  hint?: ReactNode;
  className?: string;
  /** Adds a quiet "(optional)" after the label. */
  optional?: boolean;
  /**
   * "div" for a composite control (a search box plus a list, a pop-up picker)
   * that a wrapping <label> would re-click; it is named by aria-labelledby.
   */
  as?: "label" | "div";
  children: ReactNode;
}) {
  const labelId = useId();
  const error = name && errors?.[name];
  const caption = (
    <>
      {label}
      {optional && <span className="ml-1 font-normal text-gray-400">(optional)</span>}
    </>
  );
  const message = error ? (
    <span role="alert" className="mt-1 block text-xs font-normal text-red-700">
      {error}
    </span>
  ) : (
    hint && <span className="mt-1 block text-xs font-normal leading-5 text-gray-500">{hint}</span>
  );

  if (as === "div") {
    return (
      <div role="group" aria-labelledby={labelId} className={`block text-sm font-medium text-gray-700 ${className}`}>
        <span id={labelId} className="mb-1 block">{caption}</span>
        {children}
        {message}
      </div>
    );
  }
  return (
    <label className={`block text-sm font-medium text-gray-700 ${className}`}>
      {caption}
      {children}
      {message}
    </label>
  );
}

const COLUMNS = {
  1: "",
  2: "sm:grid-cols-2",
  3: "sm:grid-cols-2 lg:grid-cols-3",
  4: "sm:grid-cols-2 lg:grid-cols-4",
} as const;

export function FormSection({
  title,
  description,
  columns = 2,
  divided = true,
  children,
}: {
  title: string;
  description?: string;
  columns?: keyof typeof COLUMNS;
  /** A rule above the section; off for the first section in a card. */
  divided?: boolean;
  children: ReactNode;
}) {
  return (
    <section className={divided ? "border-t border-gray-100 pt-5" : ""}>
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      {description && (
        <p className="mt-0.5 text-xs text-gray-500">{description}</p>
      )}
      <div className={`mt-3 grid gap-4 ${COLUMNS[columns]}`}>{children}</div>
    </section>
  );
}
